import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

import { BreadcrumbItem, BreadcrumbPage } from '~/components/ui/breadcrumb';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger
} from '~/components/ui/dropdown-menu';
import { useActiveDepartment } from '~/hooks/use-active-department';
import { meQueryOptions } from '~/lib/auth';
import { departmentsQueryOptions } from '~/lib/departments';

interface DepartmentBreadcrumbProps {
  orgSlug: string;
  deptSlug: string;
}

export function DepartmentBreadcrumb({
  orgSlug: _orgSlug,
  deptSlug
}: DepartmentBreadcrumbProps): React.ReactNode {
  const router = useRouter();
  const { data } = useQuery(departmentsQueryOptions);
  const { data: session } = useQuery(meQueryOptions);
  const [, setActiveDepartment] = useActiveDepartment();
  const [open, setOpen] = useState(false);

  const departments = data?.departments ?? [];
  const activeDept = departments.find((d) => d.slug === deptSlug);

  if (departments.length <= 1) {
    return (
      <BreadcrumbItem>
        <BreadcrumbPage>{activeDept?.name ?? deptSlug}</BreadcrumbPage>
      </BreadcrumbItem>
    );
  }

  return (
    <BreadcrumbItem>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger className="flex items-center gap-1 text-foreground hover:text-foreground/80 transition-colors">
          <span className="font-normal">{activeDept?.name ?? deptSlug}</span>
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuRadioGroup
            value={deptSlug}
            onValueChange={(value: string) => {
              const dept = departments.find((d) => d.slug === value);
              if (dept && session) {
                setOpen(false);
                setActiveDepartment(value);
                const { pathname, searchStr } = router.state.location;
                const newPathname = pathname.replace(
                  new RegExp(`/department/${deptSlug}(?=/|$)`),
                  `/department/${value}`
                );
                router.history.push(newPathname + searchStr);
              }
            }}
          >
            {departments.map((dept) => (
              <DropdownMenuRadioItem key={dept.id} value={dept.slug}>
                {dept.name}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </BreadcrumbItem>
  );
}
